/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { OptionType } from 'client-code/recordings';

import { DeviceModel } from '../../../core/device';
import { Locale, LocaleKeys } from '../../../core/locale';
import { SharedPrefConstants } from '../../../core/storage';
import {
    TOverlayOptions,
    TRecordingOptions,
    TRecordingOverlayIds,
    TRecordingValues,
    TRecordingValuesWithDisplayOptions,
} from '../../../core/types';
import { RecordingOverlayOptionType } from './RecordingOverlayOptionType';

export class RecordingOptionsGenerator {
    private static mRecordingOptionsMap: Map<string, any> = new Map();

    private static mRecordingOverlayOptionsValue =
        RecordingOptionsGenerator.getDefaultRecordingOverlayOptionsValue();

    private static mRecordingValues: TRecordingValues;

    private mOverlayOptions: Array<TOverlayOptions> = [];

    private static actionKernelType: string;

    public constructor(
        recordingOptions: TRecordingOptions,
        recordingDisplayOptions: TRecordingValuesWithDisplayOptions,
        recordingType: string,
        recordingOverlayIds: TRecordingOverlayIds,
        actionKernelType: string,
    ) {
        RecordingOptionsGenerator.actionKernelType = actionKernelType;
        RecordingOptionsGenerator.mRecordingValues =
            RecordingOptionsGenerator.getDefaultTRecordingValues();
        if (recordingDisplayOptions.recordingValuesWithDisplayOptions.recordingValues) {
            RecordingOptionsGenerator.mRecordingValues.recordingValues =
                recordingDisplayOptions.recordingValuesWithDisplayOptions.recordingValues;
        }
        this.initOverlayOptions(recordingOptions, recordingType, recordingOverlayIds);
        this.setDefaultSelectedDisplayOptions(
            recordingDisplayOptions.recordingValuesWithDisplayOptions.recordingValues,
        );
        this.populateOverlayOptions();
    }

    // eslint-disable-next-line class-methods-use-this
    private static clearRecordingOptions() {
        RecordingOptionsGenerator.mRecordingOptionsMap.clear();
    }

    /**
     * Resets the recording overlay options to their default values.
     */
    private static getDefaultRecordingOverlayOptionsValue(): any {
        return {
            /* Note that the items are arranged in the order
            they need to be generated, not alphabetically. */

            TIME_AND_CHANNEL: {
                text: '',
                visible: false,
                value: {
                    actionType: RecordingOverlayOptionType.TIME_AND_CHANNEL,
                    optionList: [],
                },
                selectedIndex: -1,
            },
            KEEP_UNTIL: {
                text: '',
                visible: false,
                value: { actionType: RecordingOverlayOptionType.KEEP_UNTIL, optionList: [] },
                selectedIndex: -1,
            },
            START_RECORDING: {
                text: '',
                visible: false,
                value: {
                    actionType: RecordingOverlayOptionType.START_RECORDING,
                    optionList: [],
                },
                selectedIndex: -1,
            },
            STOP_RECORDING: {
                text: '',
                visible: false,
                value: {
                    actionType: RecordingOverlayOptionType.STOP_RECORDING,
                    optionList: [],
                },
                selectedIndex: -1,
            },
        };
    }

    /**
     * Returns the default values for `TRecordingValues`, providing consistent default values for
     * recordings.
     *
     * @returns {TRecordingValues} The default configuration values for recording.
     */
    private static getDefaultTRecordingValues(): TRecordingValues {
        return {
            recordingValues: {
                timeAndChannel: {},
                startRecordingEarly: {},
                stopRecordingLate: {},
            },
        };
    }

    /**
     * Initializes overlay options based on the provided
     * recording options, recording type, and guide cell.
     *
     * @param recordingOptions
     * @param recordingType
     * @param guideCell
     */
    private initOverlayOptions(
        recordingOptions: TRecordingOptions,
        recordingType: string,
        recordingOverlayIds: TRecordingOverlayIds,
    ): void {
        const kernelType = RecordingOptionsGenerator.getActionKernelType();
        const match = kernelType.match(/^recordThis(.*)ActionKernel$/);
        let recordType;
        if (match && match[1]) {
            recordType = match[1].toLowerCase();
        }
        const recordingTypes = new Set(['episode', 'movie', 'show', 'game']);

        let recordingTypeText: string;

        if (recordingTypes.has(recordType)) {
            const recordingTypeKey = `RECORDING_OVERLAY_OPTION_THIS_${recordType.toUpperCase()}`;
            recordingTypeText = Locale.getString(LocaleKeys[recordingTypeKey]);
        } else {
            recordingTypeText = '';
        }
        const recordText =
            recordingTypeText.length > 0
                ? `${Locale.getString(LocaleKeys.RECORDING_OVERLAY_OPTION_RECORD)} ${recordingTypeText} ${Locale.getString(LocaleKeys.RECORDING_OVERLAY_OPTION_WITH_THESE_OPTIONS)}`
                : `${Locale.getString(LocaleKeys.RECORDING_OVERLAY_OPTION_RECORD_THIS)} ${recordingType} ${Locale.getString(LocaleKeys.RECORDING_OVERLAY_OPTION_WITH_THESE_OPTIONS)}`;

        const cancelText =
            recordingTypeText.length > 0
                ? `${Locale.getString(LocaleKeys.RECORDING_OVERLAY_OPTION_CANCEL)} ${recordingTypeText})`
                : `${Locale.getString(LocaleKeys.RECORDING_OVERLAY_OPTION_CANCEL_THIS)} ${recordingType})`;
        RecordingOptionsGenerator.setRecordingOptions('guideCellData', recordingOverlayIds);
        this.mOverlayOptions.push({
            text: recordText,
            value: {
                actionType: RecordingOverlayOptionType.SCHEDULE_RECORDING,
            },
        });
        const recordingCancelActionId = DeviceModel.getSharedPreferences(
            SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
        ).getString(SharedPrefConstants.RECORDING_CANCEL_ACTION_ID);
        if (RecordingOptionsGenerator.actionKernelType.includes('record')) {
            this.mOverlayOptions.push({
                text: cancelText,
                value: {
                    actionType: RecordingOverlayOptionType.CANCEL,
                },
            });
        } else {
            this.mOverlayOptions.push({
                text: `${Locale.getString(LocaleKeys.RECORDING_OVERLAY_OPTION_NO_CHANGE_IN_RECORDING_OPTION)}`,
                value: {
                    actionType: RecordingOverlayOptionType.NO_CHANGE_IN_RECORDING_OPTION,
                },
            });
            this.mOverlayOptions.push({
                text: `${Locale.getString(LocaleKeys.RECORDING_OVERLAY_OPTION_CANCEL_RECORDING)}`,
                value: {
                    actionType: RecordingOverlayOptionType[recordingCancelActionId],
                    recordType: recordingType,
                },
            });
        }

        Object.keys(recordingOptions.recordingOptions).forEach((key) => {
            const { visibility, optionList, optionListType } =
                recordingOptions.recordingOptions[key];

            switch (optionListType) {
                case OptionType.TimeAndChannel:
                    // eslint-disable-next-line max-len
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.TIME_AND_CHANNEL.visible =
                        visibility;
                    // eslint-disable-next-line max-len
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.TIME_AND_CHANNEL.value.optionList =
                        optionList;
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.TIME_AND_CHANNEL.text =
                        Locale.getString(LocaleKeys.RECORDING_OVERLAY_OPTION_TIME_AND_CHANNEL);
                    RecordingOptionsGenerator.setRecordingOptions('timeAndChannel', optionList);
                    break;

                case OptionType.KeepUntil:
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.KEEP_UNTIL.visible =
                        visibility;
                    // eslint-disable-next-line max-len
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.KEEP_UNTIL.value.optionList =
                        optionList;
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.KEEP_UNTIL.text =
                        Locale.getString(LocaleKeys.RECORDING_OVERLAY_OPTION_KEEP_UNTIL);
                    RecordingOptionsGenerator.setRecordingOptions('keepUntil', optionList);
                    break;

                case OptionType.StartRecording:
                    // eslint-disable-next-line max-len
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.START_RECORDING.visible =
                        visibility;
                    // eslint-disable-next-line max-len
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.START_RECORDING.value.optionList =
                        optionList;
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.START_RECORDING.text =
                        Locale.getString(LocaleKeys.RECORDING_OVERLAY_OPTION_START_RECORDING);
                    RecordingOptionsGenerator.setRecordingOptions(
                        'startRecordingEarly',
                        optionList,
                    );
                    break;

                case OptionType.StopRecording:
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.STOP_RECORDING.visible =
                        visibility;
                    // eslint-disable-next-line max-len
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.STOP_RECORDING.value.optionList =
                        optionList;
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.STOP_RECORDING.text =
                        Locale.getString(LocaleKeys.RECORDING_OVERLAY_OPTION_STOP_RECORDING);
                    RecordingOptionsGenerator.setRecordingOptions('stopRecordingLate', optionList);
                    break;

                default:
            }
        });
    }

    /**
     * It will push the all recording options into the overlay list
     */
    private populateOverlayOptions(): void {
        const recordingOverlayOptionKeys = Object.keys(
            RecordingOptionsGenerator.mRecordingOverlayOptionsValue,
        );
        for (let i = 0; i < recordingOverlayOptionKeys.length; i += 1) {
            const index = recordingOverlayOptionKeys[i];
            if (
                RecordingOptionsGenerator.mRecordingOverlayOptionsValue[index].visible &&
                RecordingOptionsGenerator.mRecordingOverlayOptionsValue[index].value
            ) {
                let horizontalOptions: string[] = [];
                for (
                    let j = 0;
                    j <
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue[index].value.optionList
                        .length;
                    j += 1
                ) {
                    horizontalOptions.push(
                        RecordingOptionsGenerator.mRecordingOverlayOptionsValue[index].value
                            .optionList[j].label,
                    );
                }
                this.mOverlayOptions.push({
                    text: RecordingOptionsGenerator.mRecordingOverlayOptionsValue[index].text,
                    horizontalSelectionoptions: horizontalOptions,
                    value: RecordingOptionsGenerator.mRecordingOverlayOptionsValue[index].value,
                    initialHorizontalIndex:
                        RecordingOptionsGenerator.mRecordingOverlayOptionsValue[index]
                            .selectedIndex,
                });
                horizontalOptions = [];
            }
        }
    }

    /**
     * Sets the default selected items index for the given overlay options.
     * @param recordingValues
     */
    // eslint-disable-next-line class-methods-use-this
    private setDefaultSelectedDisplayOptions(recordingValues: any): void {
        Object.keys(recordingValues).forEach((optionList) => {
            const optionListData = RecordingOptionsGenerator.getRecordingOptions(optionList);
            switch (optionList) {
                case 'keepUntil':
                    // eslint-disable-next-line max-len
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.KEEP_UNTIL.selectedIndex =
                        optionListData.findIndex((option) => {
                            return option.keepUntil === recordingValues.keepUntil;
                        });
                    break;

                case 'startRecordingEarly':
                    // eslint-disable-next-line max-len
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.START_RECORDING.selectedIndex =
                        optionListData.findIndex((option) => {
                            return (
                                option.startRecording.type ===
                                    recordingValues?.startRecordingEarly?.type &&
                                option.startRecording?.minutes ===
                                    recordingValues.startRecordingEarly?.minutes
                            );
                        });
                    break;

                case 'stopRecordingLate':
                    // eslint-disable-next-line max-len
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.STOP_RECORDING.selectedIndex =
                        optionListData.findIndex((option) => {
                            return (
                                option.stopRecording?.type ===
                                    recordingValues.stopRecordingLate?.type &&
                                option.stopRecording?.minutes ===
                                    recordingValues.stopRecordingLate?.minutes
                            );
                        });
                    break;

                case 'timeAndChannel':
                    // eslint-disable-next-line max-len
                    RecordingOptionsGenerator.mRecordingOverlayOptionsValue.TIME_AND_CHANNEL.selectedIndex =
                        optionListData.findIndex((option) => {
                            return (
                                option.timeAndChannel?.offerId ===
                                recordingValues.timeAndChannel?.offerId
                            );
                        });

                    break;

                default:
            }
        });
    }

    public static getActionKernelType(): string {
        return RecordingOptionsGenerator.actionKernelType;
    }

    /**
     * Returns a list of available overlay options.
     * @returns `TOverlayOptions[]`: An array of overlay options.
     */
    public getOverlayOptions(): TOverlayOptions[] {
        return this.mOverlayOptions;
    }

    public static getRecordingOptions(key: string) {
        return RecordingOptionsGenerator.mRecordingOptionsMap.get(key);
    }

    /**
     * Retrieves recording values based on the provided options.
     * @param options
     * @returns `TRecordingValues`: The selected recording values
     */
    public static getRecordingValuesForSelectedOption(options: any): TRecordingValues {
        options.forEach((optionListData) => {
            switch (optionListData.value.actionType) {
                case RecordingOverlayOptionType.KEEP_UNTIL:
                    RecordingOptionsGenerator.mRecordingValues.recordingValues.keepUntil =
                        RecordingOptionsGenerator.getRecordingOptions('keepUntil')[
                            // eslint-disable-next-line max-len
                            RecordingOptionsGenerator.mRecordingOverlayOptionsValue.KEEP_UNTIL.selectedIndex
                        ].keepUntil;
                    break;
                case RecordingOverlayOptionType.START_RECORDING:
                    RecordingOptionsGenerator.mRecordingValues.recordingValues.startRecordingEarly =
                        RecordingOptionsGenerator.getRecordingOptions('startRecordingEarly')[
                            // eslint-disable-next-line max-len
                            RecordingOptionsGenerator.mRecordingOverlayOptionsValue.START_RECORDING.selectedIndex
                        ].startRecording;
                    break;
                case RecordingOverlayOptionType.STOP_RECORDING:
                    RecordingOptionsGenerator.mRecordingValues.recordingValues.stopRecordingLate =
                        RecordingOptionsGenerator.getRecordingOptions('stopRecordingLate')[
                            // eslint-disable-next-line max-len
                            RecordingOptionsGenerator.mRecordingOverlayOptionsValue.STOP_RECORDING.selectedIndex
                        ].stopRecording;
                    break;
                case RecordingOverlayOptionType.TIME_AND_CHANNEL:
                    RecordingOptionsGenerator.mRecordingValues.recordingValues.timeAndChannel =
                        RecordingOptionsGenerator.getRecordingOptions('timeAndChannel')[
                            // eslint-disable-next-line max-len
                            RecordingOptionsGenerator.mRecordingOverlayOptionsValue.TIME_AND_CHANNEL.selectedIndex
                        ].timeAndChannel;
                    break;
                default:
            }
        });
        return RecordingOptionsGenerator.mRecordingValues;
    }

    /**
     * Reset all recording options to default values
     */
    public static reset(): void {
        RecordingOptionsGenerator.mRecordingOverlayOptionsValue =
            RecordingOptionsGenerator.getDefaultRecordingOverlayOptionsValue();
        RecordingOptionsGenerator.mRecordingValues =
            RecordingOptionsGenerator.getDefaultTRecordingValues();
        RecordingOptionsGenerator.clearRecordingOptions();
    }

    public static setRecordingOptions(key: string, value: any) {
        RecordingOptionsGenerator.mRecordingOptionsMap.set(key, value);
    }

    /**
     * Updates the selected index for the recording options overlay
     * @param selectedOption
     * @param overlayHighlightHorizontalPosition
     * @param overlayHighlightVerticalPosition
     */
    public static updateRecordingOptionsOverlaySelectedIndex(
        selectedOption: any,
        overlayHighlightHorizontalPosition,
        overlayHighlightVerticalPosition,
    ): void {
        switch (selectedOption.value.actionType) {
            case RecordingOverlayOptionType.START_RECORDING:
                // eslint-disable-next-line max-len
                RecordingOptionsGenerator.mRecordingOverlayOptionsValue.START_RECORDING.selectedIndex =
                    overlayHighlightHorizontalPosition[overlayHighlightVerticalPosition];
                break;
            case RecordingOverlayOptionType.STOP_RECORDING:
                // eslint-disable-next-line max-len
                RecordingOptionsGenerator.mRecordingOverlayOptionsValue.STOP_RECORDING.selectedIndex =
                    overlayHighlightHorizontalPosition[overlayHighlightVerticalPosition];
                break;
            case RecordingOverlayOptionType.TIME_AND_CHANNEL:
                // eslint-disable-next-line max-len
                RecordingOptionsGenerator.mRecordingOverlayOptionsValue.TIME_AND_CHANNEL.selectedIndex =
                    overlayHighlightHorizontalPosition[overlayHighlightVerticalPosition];
                break;
            case RecordingOverlayOptionType.KEEP_UNTIL:
                RecordingOptionsGenerator.mRecordingOverlayOptionsValue.KEEP_UNTIL.selectedIndex =
                    overlayHighlightHorizontalPosition[overlayHighlightVerticalPosition];
                break;
            default:
        }
    }
}
